import React, { useCallback, useEffect, useRef, useState } from 'react';
import GoogleOAuthMark from './GoogleOAuthMark';
import MagicLinkPostSendModal from './MagicLinkPostSendModal';

const MAGIC_SUCCESS_STATUSES = new Set(['sent', 'success']);
const MAGIC_PENDING_STATUSES = new Set(['sending', 'pending']);

const isMagicSuccessStatus = (status) =>
  MAGIC_SUCCESS_STATUSES.has(String(status || '').toLowerCase());

const isMagicPendingStatus = (status) =>
  MAGIC_PENDING_STATUSES.has(String(status || '').toLowerCase());

/**
 * Unified auth chooser for LocalOps flows.
 * Defaults to account-first. LocalOps action-intent callers can opt into
 * magic-link-first with primaryAuthMethod="magic_link".
 */
export default function AuthChooser({
  headerText,
  subText,
  primaryAuthMethod = 'account',
  primaryLabel = 'Use Joe Galaxy Account',
  primaryHelper1 = 'Recommended • Earn +1000 JGK bonus',
  primaryHelper2 = 'Faster access, saved history, bonus JGK for verified actions.',
  onPrimary,
  primaryDisabled = false,
  onPrimaryStart = () => {},
  email,
  onEmailChange,
  emailError,
  onEmailErrorClear = () => {},
  onSendMagicLink,
  magicStatus = 'idle',
  googleOAuthEnabled = false,
  googleOAuthHref = '',
}) {
  const magicLinkFirst = primaryAuthMethod === 'magic_link';
  const safeGoogleOAuthHref = String(googleOAuthHref || '').trim();
  const showGoogleOAuth = Boolean(googleOAuthEnabled && safeGoogleOAuthHref);
  const emailInputRef = useRef(null);
  const previousMagicStatusRef = useRef(magicStatus);
  const awaitingUserSendRef = useRef(false);
  const sawPendingForUserSendRef = useRef(false);
  const submittedEmailRef = useRef('');
  const [postSendModalOpen, setPostSendModalOpen] = useState(false);
  const [successfulSendCount, setSuccessfulSendCount] = useState(0);
  const [postSendEmail, setPostSendEmail] = useState('');
  const [editingAfterSent, setEditingAfterSent] = useState(false);
  const magicPending = isMagicPendingStatus(magicStatus);
  const magicSuccess = isMagicSuccessStatus(magicStatus);
  const showResendMode = magicSuccess && !editingAfterSent;
  const magicButtonText =
    magicPending
      ? 'Sending...'
      : showResendMode
      ? 'Resend magic link'
      : 'Send magic link';

  useEffect(() => {
    const previousMagicStatus = previousMagicStatusRef.current;

    if (awaitingUserSendRef.current && isMagicPendingStatus(magicStatus)) {
      sawPendingForUserSendRef.current = true;
    }

    if (
      awaitingUserSendRef.current &&
      isMagicSuccessStatus(magicStatus) &&
      (sawPendingForUserSendRef.current ||
        isMagicPendingStatus(previousMagicStatus))
    ) {
      setPostSendEmail(submittedEmailRef.current || String(email || '').trim());
      setSuccessfulSendCount((count) => count + 1);
      setPostSendModalOpen(true);
      setEditingAfterSent(false);
      awaitingUserSendRef.current = false;
      sawPendingForUserSendRef.current = false;
    }

    if (
      awaitingUserSendRef.current &&
      !isMagicPendingStatus(magicStatus) &&
      !isMagicSuccessStatus(magicStatus)
    ) {
      awaitingUserSendRef.current = false;
      sawPendingForUserSendRef.current = false;
    }

    previousMagicStatusRef.current = magicStatus;
  }, [magicStatus, email]);

  const submitMagicLink = useCallback(
    (event) => {
      event?.preventDefault?.();
      setEditingAfterSent(false);
      awaitingUserSendRef.current = true;
      sawPendingForUserSendRef.current = isMagicPendingStatus(magicStatus);
      submittedEmailRef.current = String(email || '').trim();
      onSendMagicLink?.();
    },
    [email, magicStatus, onSendMagicLink],
  );

  const handleUseDifferentEmail = useCallback(() => {
    setPostSendModalOpen(false);
    setEditingAfterSent(true);
    awaitingUserSendRef.current = false;
    sawPendingForUserSendRef.current = false;
    setTimeout(() => {
      emailInputRef.current?.focus?.();
    }, 0);
  }, []);

  const handleEmailChange = (value) => {
    onEmailChange?.(value);
    if (emailError) onEmailErrorClear();
    if (magicSuccess) {
      setEditingAfterSent(true);
    }
  };

  const renderPostSendModal = () => (
    <MagicLinkPostSendModal
      email={postSendEmail || email}
      open={postSendModalOpen}
      resendCount={successfulSendCount}
      showGoogleOAuth={showGoogleOAuth}
      googleOAuthHref={safeGoogleOAuthHref}
      onUseDifferentEmail={handleUseDifferentEmail}
      onResend={submitMagicLink}
      sending={magicPending}
    />
  );
  const renderGoogleOAuthOption = () => {
    if (!showGoogleOAuth) return null;
    return (
      <div
        style={{
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.035)',
          border: '1px solid rgba(255,255,255,0.08)',
          marginTop: magicLinkFirst ? 0 : 10,
          marginBottom: magicLinkFirst ? 10 : 0,
        }}
        data-testid="authchooser-google-oauth"
      >
        <a
          href={safeGoogleOAuthHref}
          className="loc-btn secondary localops-btn"
          style={{
            width: '100%',
            minHeight: 48,
            padding: '12px 14px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            color: '#e8edf7',
            fontWeight: 700,
            border: '1px solid rgba(255,255,255,0.22)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
            gap: 8,
            boxSizing: 'border-box',
          }}
          data-testid="authchooser-google-oauth-link"
        >
          <GoogleOAuthMark
            style={{ width: 18, height: 18, flex: '0 0 auto' }}
          />
          <span>Continue with Google</span>
        </a>
        <div
          style={{
            color: '#9fb6d1',
            fontSize: 11,
            fontWeight: 500,
            lineHeight: 1.4,
            marginTop: 8,
          }}
        >
          Secure Google sign-in. We'll bring you back here.
        </div>
      </div>
    );
  };

  if (!magicLinkFirst) {
    return (
      <>
        <div style={{ width: '100%' }}>
        {headerText ? (
          <div
            style={{
              marginBottom: 8,
              color: '#e8edf7',
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {headerText}
          </div>
        ) : null}
        {subText ? (
          <div
            style={{
              marginBottom: 10,
              color: '#9fb6d1',
              fontSize: 13,
            }}
          >
            {subText}
          </div>
        ) : null}

        <button
          type="button"
          className="loc-btn"
          disabled={primaryDisabled}
          onClick={() => {
            onPrimaryStart();
            if (onPrimary) onPrimary();
          }}
          style={{
            width: '100%',
            marginBottom: 10,
            padding: '12px 14px',
            borderRadius: 12,
            background:
              'linear-gradient(135deg, rgba(25,147,255,0.9), rgba(79,106,255,0.9))',
            color: '#fff',
            fontWeight: 800,
            border: 'none',
            opacity: primaryDisabled ? 0.65 : 1,
            cursor: primaryDisabled ? 'not-allowed' : 'pointer',
          }}
          data-testid="authchooser-account-primary"
        >
          {primaryLabel}
          {primaryHelper1 ? (
            <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.9 }}>
              {primaryHelper1}
            </div>
          ) : null}
          {primaryHelper2 ? (
            <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.75 }}>
              {primaryHelper2}
            </div>
          ) : null}
        </button>

        <form
          onSubmit={submitMagicLink}
          noValidate
          style={{
            width: '100%',
            padding: '12px 14px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            marginTop: 4,
          }}
          data-testid="authchooser-magic-secondary"
        >
          <div style={{ marginBottom: 8, color: '#c6d5ef', fontWeight: 600 }}>
            Continue with Email (Magic Link)
          </div>
          <label
            htmlFor="magic-email"
            style={{
              display: 'block',
              fontSize: 12,
              color: '#8fa3c4',
              marginBottom: 6,
            }}
          >
            Email address (no password required)
          </label>
          <input
            ref={emailInputRef}
            id="magic-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            style={{
              width: '100%',
              borderRadius: 10,
              border: `1px solid ${
                emailError ? 'rgba(255,99,99,0.7)' : 'rgba(255,255,255,0.18)'
              }`,
              background: 'rgba(255,255,255,0.06)',
              color: '#e8edf7',
              padding: '12px 12px',
              outline: 'none',
              fontSize: 14,
              marginBottom: 8,
            }}
          />
          {emailError ? (
            <div style={{ color: '#ff9b9b', fontSize: 12, marginBottom: 8 }}>
              {emailError}
            </div>
          ) : null}
          <button
            type="submit"
            className="loc-btn secondary localops-btn"
            disabled={magicPending}
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 12,
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.14))',
              color: '#e8edf7',
              border: '1px solid rgba(255,255,255,0.5)',
              fontWeight: 700,
            }}
          >
            {magicButtonText}
            <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.9 }}>
              We’ll email a link that brings you back here.
            </div>
          </button>
        </form>
        {renderGoogleOAuthOption()}
        </div>
        {renderPostSendModal()}
      </>
    );
  }

  const accountButtonStyle = (isPrimary) =>
    isPrimary
      ? {
          width: '100%',
          marginBottom: 10,
          padding: '12px 14px',
          borderRadius: 12,
          background:
            'linear-gradient(135deg, rgba(25,147,255,0.9), rgba(79,106,255,0.9))',
          color: '#fff',
          fontWeight: 800,
          border: 'none',
          opacity: primaryDisabled ? 0.65 : 1,
          cursor: primaryDisabled ? 'not-allowed' : 'pointer',
        }
      : {
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.04)',
          color: '#e8edf7',
          fontWeight: 700,
          border: '1px solid rgba(255,255,255,0.22)',
          opacity: primaryDisabled ? 0.65 : 1,
          cursor: primaryDisabled ? 'not-allowed' : 'pointer',
        };

  const magicButtonStyle = (isPrimary) =>
    isPrimary
      ? {
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          background:
            'linear-gradient(135deg, rgba(25,147,255,0.9), rgba(79,106,255,0.9))',
          color: '#fff',
          border: 'none',
          fontWeight: 800,
        }
      : {
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.14))',
          color: '#e8edf7',
          border: '1px solid rgba(255,255,255,0.5)',
          fontWeight: 700,
        };

  const renderAccountOption = (isPrimary) => {
    const button = (
      <button
        type="button"
        className={isPrimary ? 'loc-btn' : 'loc-btn secondary localops-btn'}
        disabled={primaryDisabled}
        onClick={() => {
          onPrimaryStart();
          if (onPrimary) onPrimary();
        }}
        style={accountButtonStyle(isPrimary)}
        data-testid={
          isPrimary ? 'authchooser-account-primary' : 'authchooser-account-secondary'
        }
      >
        {primaryLabel}
        {isPrimary && primaryHelper1 ? (
          <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.9 }}>
            {primaryHelper1}
          </div>
        ) : null}
        {primaryHelper2 ? (
          <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.75 }}>
            {primaryHelper2}
          </div>
        ) : null}
      </button>
    );

    if (isPrimary) return button;

    return (
      <div
        style={{
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.035)',
          border: '1px solid rgba(255,255,255,0.08)',
          marginTop: 10,
        }}
      >
        <div style={{ marginBottom: 8, color: '#c6d5ef', fontWeight: 600 }}>
          Already use Joe Galaxy?
        </div>
        {button}
      </div>
    );
  };

  const renderMagicLinkOption = (isPrimary) => (
    <form
      onSubmit={submitMagicLink}
      noValidate
      style={{
        width: '100%',
        padding: '12px 14px',
        borderRadius: 12,
        background: isPrimary
          ? 'rgba(25,147,255,0.08)'
          : 'rgba(255,255,255,0.04)',
        border: isPrimary
          ? '1px solid rgba(116,185,255,0.32)'
          : '1px solid rgba(255,255,255,0.08)',
        marginTop: isPrimary ? 0 : 4,
        marginBottom: isPrimary ? 10 : 0,
      }}
      data-testid={
        isPrimary ? 'authchooser-magic-primary' : 'authchooser-magic-secondary'
      }
    >
      <div style={{ marginBottom: 8, color: '#c6d5ef', fontWeight: 600 }}>
        {isPrimary ? 'Continue with Email' : 'Continue with Email (Magic Link)'}
      </div>
      <div
        style={{
          color: '#9fb6d1',
          fontSize: 12,
          marginBottom: 8,
          lineHeight: 1.4,
        }}
      >
        {isPrimary
          ? "We'll email a secure link that brings you back to this LocalOps action."
          : "We'll email a link that brings you back here."}
      </div>
      <label
        htmlFor="magic-email"
        style={{
          display: 'block',
          fontSize: 12,
          color: '#8fa3c4',
          marginBottom: 6,
        }}
      >
        Email address (no password required)
      </label>
      <input
        ref={emailInputRef}
        id="magic-email"
        name="email"
        type="email"
        value={email}
        onChange={(e) => handleEmailChange(e.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck="false"
        style={{
          width: '100%',
          borderRadius: 10,
          border: `1px solid ${
            emailError ? 'rgba(255,99,99,0.7)' : 'rgba(255,255,255,0.18)'
          }`,
          background: 'rgba(255,255,255,0.06)',
          color: '#e8edf7',
          padding: '12px 12px',
          outline: 'none',
          fontSize: 14,
          marginBottom: 8,
        }}
      />
      {emailError ? (
        <div style={{ color: '#ff9b9b', fontSize: 12, marginBottom: 8 }}>
          {emailError}
        </div>
      ) : null}
      <button
        type="submit"
        className={isPrimary ? 'loc-btn' : 'loc-btn secondary localops-btn'}
        disabled={magicPending}
        style={magicButtonStyle(isPrimary)}
      >
        {magicButtonText}
      </button>
    </form>
  );

  return (
    <>
      <div style={{ width: '100%' }}>
      {headerText ? (
        <div
          style={{
            marginBottom: 8,
            color: '#e8edf7',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {headerText}
        </div>
      ) : null}
      {subText ? (
        <div
          style={{
            marginBottom: 10,
            color: '#9fb6d1',
            fontSize: 13,
          }}
        >
          {subText}
        </div>
      ) : null}

      {magicLinkFirst ? (
        <>
          {renderGoogleOAuthOption()}
          {renderMagicLinkOption(true)}
          {renderAccountOption(false)}
        </>
      ) : (
        <>
          {renderAccountOption(true)}
          {renderMagicLinkOption(false)}
          {renderGoogleOAuthOption()}
        </>
      )}
      </div>
      {renderPostSendModal()}
    </>
  );
}
